import '@/types/utils/AwsWafIntegration'

import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios'
import getConfig from 'next/config'

import { AwsWafUtils } from '@/lib/AwsWaf/AwsWafUtils'
import logger from '@/lib/logger'
import { ApiRoute } from '@/utils/Api/ApiRoutes'

const { publicRuntimeConfig } = getConfig()

interface CustomRequestConfig {
  url: ApiRoute
  method: Method
  data?: any
  params?: any
  config?: AxiosRequestConfig
  metadata?: Record<string, any>
}

class Api {
  private API_URL: string

  constructor() {
    this.API_URL =
      typeof window === 'undefined' ? publicRuntimeConfig.MIDDLEWARE_INTERNAL_URL : publicRuntimeConfig.MIDDLEWARE_URL
  }

  private async getMetadata() {
    if (typeof window === 'undefined') {
      // server side, no need to add WAF token
      return {}
    }
    try {
      const token = await AwsWafUtils.getToken()
      return {
        headers: {
          'x-aws-waf-token': token
        }
      }
    } catch (err) {
      logger.info(err, 'Failed to retrieve AWS WAF token, proceeding without it', {
        isServer: typeof window === 'undefined',
        apiUrl: this.API_URL
      })
      return {}
    }
  }

  get = async <T>(url: string, params?: AxiosRequestConfig<never>): Promise<AxiosResponse<T>> => {
    const metadata = await this.getMetadata()
    return axios.get<T>(`${this.API_URL}/${url}`, {
      ...params,
      headers: {
        ...metadata?.headers,
        ...params?.headers
      }
    })
  }

  post = async <T, Y = T>(url: string, data: Y, params?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    const metadata = await this.getMetadata()
    return axios.post<T>(`${this.API_URL}/${url}`, data, {
      ...params,
      headers: {
        ...metadata?.headers,
        ...params?.headers
      }
    })
  }

  patch = async <T, Y = T>(url: string, data: Y, params?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    const metadata = await this.getMetadata()
    return axios.patch<T>(`${this.API_URL}/${url}`, data, {
      ...params,
      headers: {
        ...metadata?.headers,
        ...params?.headers
      }
    })
  }

  put = async <T, Y = T>(url: string, data: Y, params?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    const metadata = await this.getMetadata()
    return axios.put<T>(`${this.API_URL}/${url}`, data, {
      ...params,
      headers: {
        ...metadata?.headers,
        ...params?.headers
      }
    })
  }

  custom = async (config: CustomRequestConfig) => {
    const { url, method, data = {}, params = {}, config: axiosConfig = {}, metadata: customMetadata = {} } = config
    const metadata = await this.getMetadata()
    return axios({
      url,
      method,
      data,
      params,
      ...axiosConfig,
      headers: {
        ...metadata?.headers,
        ...customMetadata?.headers
      }
    })
  }
}

const api = new Api()

export default api
